import type { Ops, PrioridadeOps } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { OpsApi } from './ops.api.js';
import type { PermissoesOps } from './ops.permissoes.js';
import { rotuloEtapa, PRIORIDADES_ROTULO } from './etapa-ops.js';
import { formatarDataHora } from '../pedidos/formatacao.js';

/**
 * `<erp-ops-detalhe-modal>` — dados administrativos de uma OPS (técnico
 * responsável, prazo, prioridade). A mudança de ETAPA em si acontece
 * pelo arraste no quadro Kanban (`<erp-producao-page>`), não aqui — esta
 * modal só cobre `PATCH /ops/:id` (nunca `POST /ops/:id/etapa`), mesma
 * separação de responsabilidade que o backend já tem entre os dois
 * endpoints.
 *
 * "Atribuir a mim" em vez de um seletor de técnico: não existe endpoint
 * de listagem de usuários acessível ao papel `producao` (`GET /auth/
 * usuarios` é restrito a `admin`), então um dropdown de técnicos
 * exigiria expor um diretório de usuários a um papel que hoje não tem
 * essa permissão — decisão fora do escopo desta tela. Autoatribuição é
 * o fluxo real: o técnico que abre o quadro reivindica o próprio
 * cartão.
 */
export class ErpOpsDetalheModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _opsApi: OpsApi | null = null;
  private _permissoes: PermissoesOps = { ler: false, atualizar: false };
  private _usuarioAtualId: string | null = null;
  private _aoAtualizar: (() => void) | null = null;

  private aberto = false;
  private carregando = false;
  private erroCarregar = '';
  private ops: Ops | null = null;

  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set opsApi(valor: OpsApi) {
    this._opsApi = valor;
  }

  set permissoes(valor: PermissoesOps) {
    this._permissoes = valor;
  }

  set usuarioAtualId(valor: string | null) {
    this._usuarioAtualId = valor;
  }

  set aoAtualizar(valor: () => void) {
    this._aoAtualizar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaVer(opsId: string): void {
    this.aberto = true;
    this.carregando = true;
    this.erroCarregar = '';
    this.ops = null;
    this.mensagemErro = '';
    this.render();
    void this.carregar(opsId);
  }

  private async carregar(opsId: string): Promise<void> {
    if (!this._opsApi) return;
    try {
      this.ops = await this._opsApi.obterPorId(opsId);
    } catch (erro) {
      this.erroCarregar = erro instanceof ApiError ? erro.message : 'não foi possível carregar a OPS.';
    } finally {
      this.carregando = false;
      this.render();
    }
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async salvar(prazo: string, prioridade: PrioridadeOps): Promise<void> {
    if (!this._opsApi || !this.ops || this.enviando) return;

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const atualizado = await this._opsApi.atualizar(this.ops.id, {
        prazo: prazo ? new Date(prazo).toISOString() : null,
        prioridade,
      });
      this.ops = atualizado;
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async atribuirAMim(): Promise<void> {
    if (!this._opsApi || !this.ops || !this._usuarioAtualId || this.enviando) return;

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const atualizado = await this._opsApi.atualizar(this.ops.id, { tecnicoId: this._usuarioAtualId });
      this.ops = atualizado;
      this._aoAtualizar?.();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível atribuir.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .secao { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); margin-bottom: var(--pg-space-5, 24px); }
        .secao:last-child { margin-bottom: 0; }
        .secao h3 {
          margin: 0;
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .grade { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-3, 12px); }
        .campo { display: flex; flex-direction: column; gap: 2px; }
        .campo .rotulo { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .campo .valor { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        .acao-linha { display: flex; gap: var(--pg-space-3, 12px); align-items: center; flex-wrap: wrap; }
        ${FORM_FIELD_CSS}
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">OPS${this.ops ? ` #${this.ops.id.slice(0, 8)}` : ''}</span>
        <div id="conteudo">${this.renderConteudo()}</div>
        <div slot="footer">
          <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
        </div>
      </pg-modal>
    `;

    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
    this.root.querySelector('#botao-fechar')?.addEventListener('pg-click', () => this.fechar());

    this.root.querySelector('#botao-atribuir-mim')?.addEventListener('pg-click', () => void this.atribuirAMim());
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', () => {
      const prazo = this.root.querySelector<HTMLInputElement>('#prazo')?.value ?? '';
      const prioridade = (this.root.querySelector<HTMLSelectElement>('#prioridade')?.value ??
        'normal') as PrioridadeOps;
      void this.salvar(prazo, prioridade);
    });
  }

  private renderConteudo(): string {
    if (this.carregando) {
      return '<pg-empty-state variant="carregando" titulo="OPS"></pg-empty-state>';
    }
    if (this.erroCarregar) {
      return `<pg-empty-state variant="erro" titulo="OPS" descricao="${escaparHtml(this.erroCarregar)}"></pg-empty-state>`;
    }
    if (!this.ops) {
      return '<pg-empty-state variant="vazio" titulo="OPS"></pg-empty-state>';
    }

    const ops = this.ops;
    const concluida = ops.etapa === 'enviado';
    const podeEditar = this._permissoes.atualizar && !concluida;
    const prazoValor = ops.prazo ? ops.prazo.slice(0, 10) : '';

    return `
      <div class="secao">
        <h3>Geral</h3>
        <div class="grade">
          <div class="campo"><span class="rotulo">Pedido</span><span class="valor">#${escaparHtml(ops.pedidoId.slice(0, 8))}</span></div>
          <div class="campo"><span class="rotulo">Etapa</span><span class="valor">${escaparHtml(rotuloEtapa(ops.etapa))}</span></div>
          <div class="campo"><span class="rotulo">Técnico responsável</span><span class="valor">${ops.tecnicoId ? `#${escaparHtml(ops.tecnicoId.slice(0, 8))}` : 'não atribuído'}</span></div>
          ${ops.concluidoEm ? `<div class="campo"><span class="rotulo">Concluída em</span><span class="valor">${escaparHtml(formatarDataHora(ops.concluidoEm))}</span></div>` : ''}
        </div>
      </div>

      ${
        podeEditar
          ? `<div class="secao">
              <h3>Ações</h3>
              <div class="acao-linha">
                <pg-button id="botao-atribuir-mim" variant="secondary" ${this.enviando ? 'loading' : ''}>Atribuir a mim</pg-button>
              </div>
              <div class="acao-linha">
                <input id="prazo" type="date" value="${escaparHtml(prazoValor)}" ${this.enviando ? 'disabled' : ''} />
                <select id="prioridade" ${this.enviando ? 'disabled' : ''}>
                  ${PRIORIDADES_ROTULO.map((p) => `<option value="${p.valor}" ${ops.prioridade === p.valor ? 'selected' : ''}>${p.rotulo}</option>`).join('')}
                </select>
                <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>
              </div>
              <span class="erro">${escaparHtml(this.mensagemErro)}</span>
            </div>`
          : concluida
            ? `<div class="secao"><span class="campo valor">OPS concluída — dados administrativos não podem mais ser alterados.</span></div>`
            : ''
      }
    `;
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-ops-detalhe-modal', ErpOpsDetalheModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-ops-detalhe-modal': ErpOpsDetalheModal;
  }
}
